import api from "../../services/api";

const BASE_URL = "/api/payroll";
const DEFAULT_MONEY_META = {
  currency: "INR",
  storageUnit: "paise",
  displayUnit: "rupees",
  scale: 100,
};

const unwrap = (response) => ({
  data: response?.data?.data ?? response?.data ?? null,
  meta: response?.data?.meta ?? { money: DEFAULT_MONEY_META },
});

const request = async (promise) => unwrap(await promise);

export const getMoneyMeta = (meta) => meta?.money || DEFAULT_MONEY_META;

export const paiseToRupees = (value, meta) => {
  const moneyMeta = getMoneyMeta(meta);
  return Number(value || 0) / Number(moneyMeta.scale || 100);
};

export const rupeesToPaise = (value, meta) => {
  const moneyMeta = getMoneyMeta(meta);
  return Math.round(Number(value || 0) * Number(moneyMeta.scale || 100));
};

export const formatPayrollCurrency = (value, meta) => {
  const moneyMeta = getMoneyMeta(meta);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: moneyMeta.currency || "INR",
    maximumFractionDigits: 2,
  }).format(paiseToRupees(value, moneyMeta));
};

const payrollService = {
  getMeta: async () => request(api.get(`${BASE_URL}/meta`)),
  listSalaryStructures: async () =>
    request(api.get(`${BASE_URL}/salary-structures`)),
  getSalaryStructure: async (id) =>
    request(api.get(`${BASE_URL}/salary-structures/${id}`)),
  createSalaryStructure: async (payload) =>
    request(api.post(`${BASE_URL}/salary-structures`, payload)),
  updateSalaryStructure: async (id, payload) =>
    request(api.put(`${BASE_URL}/salary-structures/${id}`, payload)),
  listCompensations: async (params = {}) =>
    request(api.get(`${BASE_URL}/compensations`, { params })),
  getCompensation: async (id) =>
    request(api.get(`${BASE_URL}/compensations/${id}`)),
  assignCompensation: async (payload) =>
    request(api.post(`${BASE_URL}/compensations`, payload)),
  getStatutoryConfig: async () =>
    request(api.get(`${BASE_URL}/statutory-config`)),
  saveStatutoryConfig: async (payload) =>
    request(api.put(`${BASE_URL}/statutory-config`, payload)),
  listAdjustments: async (params = {}) =>
    request(api.get(`${BASE_URL}/adjustments`, { params })),
  createAdjustment: async (payload) =>
    request(api.post(`${BASE_URL}/adjustments`, payload)),
  listBenefits: async (params = {}) =>
    request(api.get(`${BASE_URL}/benefits`, { params })),
  createBenefit: async (payload) =>
    request(api.post(`${BASE_URL}/benefits`, payload)),
  listRuns: async () => request(api.get(`${BASE_URL}/runs`)),
  createRun: async (payload) => request(api.post(`${BASE_URL}/runs`, payload)),
  processRun: async (id) => request(api.post(`${BASE_URL}/runs/${id}/process`)),
  finalizeRun: async (id) =>
    request(api.post(`${BASE_URL}/runs/${id}/finalize`)),
  listPayslips: async (params = {}) =>
    request(api.get(`${BASE_URL}/payslips`, { params })),
  getOwnPayslip: async (payrollRunId) =>
    request(api.get(`${BASE_URL}/payslips/my`, { params: { payrollRunId } })),
  listComplianceRecords: async (params = {}) =>
    request(api.get(`${BASE_URL}/compliance-records`, { params })),
  getComplianceDashboard: async (params = {}) =>
    request(api.get(`${BASE_URL}/compliance-records/dashboard`, { params })),
  updateComplianceRecord: async (id, payload) =>
    request(api.patch(`${BASE_URL}/compliance-records/${id}`, payload)),
  listBankTransfers: async (params = {}) =>
    request(api.get(`${BASE_URL}/bank-transfers`, { params })),
  updateBankTransfer: async (id, payload) =>
    request(api.patch(`${BASE_URL}/bank-transfers/${id}`, payload)),
  getBankTransferPreview: async (payrollRunId) =>
    request(api.get(`${BASE_URL}/bank-transfers/${payrollRunId}/preview`)),
  listForm16: async (params = {}) =>
    request(api.get(`${BASE_URL}/form16`, { params })),
  getOwnForm16: async (params = {}) =>
    request(api.get(`${BASE_URL}/form16/my`, { params })),
  generateForm16: async (payload) =>
    request(api.post(`${BASE_URL}/form16/generate`, payload)),
  updateForm16: async (id, payload) =>
    request(api.patch(`${BASE_URL}/form16/${id}`, payload)),
  listFnF: async (params = {}) =>
    request(api.get(`${BASE_URL}/fnf`, { params })),
  createFnF: async (payload) => request(api.post(`${BASE_URL}/fnf`, payload)),
  updateFnF: async (id, payload) =>
    request(api.patch(`${BASE_URL}/fnf/${id}`, payload)),
  // UI Preferences
  getUIPreferences: async () => request(api.get(`${BASE_URL}/ui-preferences`)),
  updateUIPreferences: async (payload) =>
    request(api.put(`${BASE_URL}/ui-preferences`, payload)),
  updateTablePreferences: async (tableKey, payload) =>
    request(api.patch(`${BASE_URL}/ui-preferences/table/${tableKey}`, payload)),
};

export default payrollService;
